import { FunctionComponent } from "react"
import { useNavigate } from "react-router-dom"

const WelcomePage: FunctionComponent = () => {
  const navigate = useNavigate()

  const options = [
    {
      label: "Send Simulations",
      description: "Deploy simulations to your team. In 30 minutes, you'll have cognitive insights that interviews and surveys can't reveal.",
      destination: "/dashboard/recruiter-admin/users/all",
    },
    {
      label: "View Results",
      description: "Explore cognitive blueprints. See how each person thinks across the measures you've selected.",
      destination: "/dashboard/recruiter-admin/scores",
    },
    {
      label: "Develop Your Team",
      description: "Precision development paths built around actual cognitive profiles—not generic training.",
      destination: "/dashboard/recruiter-admin/microlearning",
    },
    {
      label: "Who Thinks Like This?",
      description: "Match cognitive profiles to project demands. Find who's built for the work, not just who's available.",
      destination: "/dashboard/recruiter-admin/who-thinks",
    },
    {
      label: "Team Dynamics",
      description: "See how your people think together. Reveal collaboration patterns before they play out on real projects.",
      destination: "/dashboard/recruiter-admin/team-simulations",
    },
  ]

  // Split options for balanced layout: 3 on top, 2 centered on bottom
  const topRow = options.slice(0, 3)
  const bottomRow = options.slice(3)

  return (
    <div className="min-h-full bg-gray-50 flex flex-col items-center justify-center py-16 px-8">
      <div className="w-full max-w-4xl">
        {/* Centered Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Welcome to Meseekna
          </h1>
          <p className="text-xl text-gray-500">
            Transform how you understand your team&apos;s potential—and put it to work.
          </p>
        </div>

        {/* Value Proposition Banner */}
        <div className="bg-[#4d86ff] rounded-xl p-6 mb-10 text-white text-center">
          <h2 className="text-xl font-bold mb-2">
            Cognitive Insights That Predict Performance
          </h2>
          <p className="text-white/90 leading-relaxed max-w-2xl mx-auto">
            Traditional assessments miss what matters most. Our simulations capture how your people actually think and make decisions—the cognitive patterns that determine real-world success.
          </p>
        </div>

        {/* Section Header */}
        <h3 className="text-lg font-semibold text-gray-700 mb-6 text-center">
          Where would you like to start?
        </h3>

        {/* Top Row - 3 cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {topRow.map((option) => (
            <button
              key={option.label}
              onClick={() => navigate(option.destination)}
              className="bg-white rounded-xl border-2 border-gray-200 p-6 text-center hover:border-[#4d86ff] hover:shadow-lg transition-all group"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-[#4d86ff] transition-colors">
                {option.label}
              </h3>
              <p className="text-sm text-gray-500">{option.description}</p>
            </button>
          ))}
        </div>

        {/* Bottom Row - 2 cards centered */}
        <div className="flex justify-center gap-4 mb-10">
          {bottomRow.map((option) => (
            <button
              key={option.label}
              onClick={() => navigate(option.destination)}
              className="bg-white rounded-xl border-2 border-gray-200 p-6 text-center hover:border-[#4d86ff] hover:shadow-lg transition-all group w-full md:w-[calc(33.333%-0.5rem)]"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-[#4d86ff] transition-colors">
                {option.label}
              </h3>
              <p className="text-sm text-gray-500">{option.description}</p>
            </button>
          ))}
        </div>

        {/* Social Proof Quote */}
        <div className="text-center text-gray-500 italic max-w-2xl mx-auto">
          <p className="mb-2">
            &ldquo;The simulation takes managers from the theoretical to the right now and what it means for their day to day work. It helps them to do strategic planning in a meaningful way without losing sight of what needs to be done day to day.&rdquo;
          </p>
          <p className="text-sm not-italic text-gray-400">
            — VP Talent Development, Fortune 500 Pharmaceutical
          </p>
        </div>
      </div>
    </div>
  )
}

export default WelcomePage
